import { useEffect, useMemo, useRef, useState } from 'react';

import type { Booking, BookingGarment } from '@/api/bookings';
import type { CatalogueDesign } from '@/api/masters';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { GarmentImage } from '@/components/GarmentImage';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAmount } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';
import {
  addNewWearer,
  addToNewWearer,
  addToWearer,
  amendmentMoney,
  amendmentProblems,
  canRemove,
  canSwap,
  claimedUnitIds,
  dropIncoming,
  clearSwap,
  emptyAmendment,
  incomingFrom,
  incomingUnitIds,
  pendingChanges,
  removeNewWearer,
  setSwap,
  swapWords,
  toggleRemoval,
  type Amendment,
  type IncomingGarment,
} from '@core/bookings/amendment';
import { DesignRail } from './DesignRail';
import { GarmentPicker } from './GarmentPicker';
import { garmentTone, isOffTheBooking } from '@core/bookings/tones';
import type { AvailableUnit } from '@core/bookings/rail';
import { withReference } from '@core/units/referenceNumber';

/** Where a picked garment goes: onto somebody in the party, or onto somebody being added. */
type Target =
  { kind: 'wearer'; id: number; name: string } | { kind: 'new'; key: string; name: string };

/**
 * Changing what a booking is for, on a full page.
 *
 * <p>
 * <strong>The argument is `NewBookingPage`'s, and it applies harder here.</strong> "If I swap the
 * sherwani, what does he pay?" is not a side question during an amendment — it is the entire
 * question. A 560px drawer shows the party or the delta and never both, which is how a clerk ends up
 * quoting from memory to a customer standing at the counter. The layout follows the booking screen
 * deliberately: somebody who takes bookings on that one should not have to learn a second
 * arrangement to change one.
 * </p>
 *
 * <p>
 * <strong>Presentational, like the two counters.</strong> It hands back an {@link Amendment} and
 * `BookingsPage` owns the mutation, which is what keeps it testable without a session or a query
 * client. `amendment.ts` is the whole state — the pending changes are a list with a figure beside
 * each *before* anything is posted, which is the thing a counter needs most.
 * </p>
 *
 * <p>
 * <strong>Removed garments are drawn greyed, not hidden.</strong> Hiding them would put the screen
 * exactly where a soft delete would have — and a shop looking at a booking a week later needs to see
 * that the safa was dropped, not a booking that never had one.
 * </p>
 *
 * <p>
 * <strong>It picks from the design cards, as taking a booking does</strong> (the redesign's "one way
 * to pick everywhere"): a design opens its popup, the garment is chosen for the size, and a scan
 * adds the exact one. **Swap opens the garment's own design**, because most swaps are another size
 * of the same thing; closing it leaves the swap waiting for any other design. "Show each garment"
 * keeps the serial-by-serial rail.
 * </p>
 */
export function AmendBookingPage({
  booking,
  units,
  designs = [],
  moreDesigns,
  searching = false,
  searched = false,
  onSearch,
  onClose,
  onApply,
  onChaseHold,
  busy = false,
  currency = '',
}: {
  booking: Booking;
  /** The rail for this booking's possession period. The caller owns the query. */
  units: AvailableUnit[];
  /** The same answer by design — what the design cards draw. */
  designs?: CatalogueDesign[];
  /** Said under the design cards when the catalogue holds more than one answer carries. */
  moreDesigns?: string | null | undefined;
  searching?: boolean;
  searched?: boolean;
  /** "Search the rail for this booking's dates" — the dates are the booking's and do not move. */
  onSearch: () => void;
  onClose: () => void;
  onApply: (amendment: Amendment) => void;
  onChaseHold?: ((unit: AvailableUnit) => void) | undefined;
  busy?: boolean;
  currency?: string;
}) {
  const [amendment, setAmendment] = useState<Amendment>(() => emptyAmendment());
  const [target, setTarget] = useState<Target | null>(null);

  // The garment waiting for a replacement. Separate from `target` because they are different
  // intents: one is "who is this for", the other is "what is this instead of", and sharing a slot
  // would make the next click ambiguous.
  const [swapping, setSwapping] = useState<BookingGarment | null>(null);

  const [wearerName, setWearerName] = useState('');

  // The design cards by default; the serial-by-serial rail one click away, as on the booking page.
  const [eachGarment, setEachGarment] = useState(false);
  const [openDesign, setOpenDesign] = useState<number | null>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const railUnits = useMemo(() => new Map(units.map((unit) => [unit.unitId, unit])), [units]);

  const money = amendmentMoney(booking, amendment);
  const changes = pendingChanges(booking, amendment);
  const problems = amendmentProblems(booking, amendment);
  const claimed = claimedUnitIds(booking, amendment);

  // Only what this amendment brought in can be taken back off from the rail. A garment already on
  // the booking is claimed too, but un-picking that one means removing it, which is the party
  // table's Drop button and is gated on the garment's state.
  const droppable = incomingUnitIds(amendment);

  // An amendment does not move dates, so the rail is asked for the booking's own possession period
  // and asked once. `days` is likewise the booking's charged span — an incoming garment is priced
  // over what the customer already agreed to, never over a fresh period.
  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // Who the next pick is for — the swapped garment's wearer while swapping, or the chosen person —
  // and what a swap keeps and leaves, which is not what taking a booking's swap keeps.
  const swap = swapping ? swapWords(booking, swapping) : null;
  const pickingFor = swap ? swap.forName : (target?.name ?? null);

  const startSwap = (garment: BookingGarment) => {
    setTarget(null);
    setSwapping(garment);

    // Most swaps are another size of the same design, so its popup opens straight away; closing
    // it leaves the swap waiting on the rail for any other design.
    if (!eachGarment && designs.some((design) => design.itemId === garment.itemId)) {
      setOpenDesign(garment.itemId);
    } else {
      railRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const pick = (unit: AvailableUnit) => {
    const garment = incomingFrom(unit, booking.chargedDays);

    if (swapping) {
      setAmendment((was) => setSwap(was, swapping.id, garment));
      setSwapping(null);

      return;
    }

    if (!target) {
      return;
    }

    setAmendment((was) =>
      target.kind === 'wearer'
        ? addToWearer(was, target.id, garment)
        : addToNewWearer(was, target.key, garment),
    );
  };

  const addWearer = () => {
    const name = wearerName.trim();

    if (!name) {
      return;
    }

    setAmendment((was) => {
      const next = addNewWearer(was, name);
      const added = next.newWearers[next.newWearers.length - 1]!;

      setTarget({ kind: 'new', key: added.key, name });

      return next;
    });

    setWearerName('');
  };

  /*
    The same "why can I not add" answer NewBookingPage owes, asked differently: there a garment needs
    a person, here it needs a person *or* a line to replace.
  */
  const railNotice = target ? (
    <p className="text-default mb-3 text-[12.5px]">
      Adding to <b className="text-gray-900">{target.name}</b>. Pick somebody else above to change
      that.
    </p>
  ) : (
    <Notice tone="info" title="Say who this is for first">
      Choose &ldquo;Add a garment&rdquo; on somebody in the party, or &ldquo;Swap&rdquo; on a
      garment they already have, then pick a design or scan a tag.
    </Notice>
  );

  const railToggle = (
    <button
      type="button"
      onClick={() => setEachGarment((was) => !was)}
      className="text-primary cursor-pointer text-[12.5px] font-semibold whitespace-nowrap"
    >
      {eachGarment ? 'Show designs' : 'Show each garment'}
    </button>
  );

  return (
    <div ref={fields} {...enterKeyScope}>
      {/* ---------------------------------------------------------- header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to the booking"
            className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>
          <div>
            <h1 className="page-title">Amend booking</h1>
            <p className="text-default text-[12.5px]">
              {booking.documentNumber || `#${booking.id}`} · {booking.customerName} ·{' '}
              {booking.chargedDays} day{booking.chargedDays === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        <Button variant="secondary" shortcut="back" onClick={onClose}>
          Cancel
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* ------------------------------------------------------ left column */}
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <Card>
            <SectionTitle>The party as it stands</SectionTitle>

            {booking.wearers.map((wearer) => (
              <div
                key={wearer.id}
                className="border-border-color mb-3 rounded border p-3 last:mb-0"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-gray-900">
                    {wearer.name}
                    {wearer.relation && (
                      <span className="text-default font-normal"> · {wearer.relation}</span>
                    )}
                  </span>

                  <button
                    type="button"
                    onClick={() => {
                      setSwapping(null);
                      setTarget({ kind: 'wearer', id: wearer.id, name: wearer.name });
                    }}
                    className={`rounded border px-2 py-1 text-[11.5px] ${
                      target?.kind === 'wearer' && target.id === wearer.id
                        ? 'border-primary text-primary bg-primary-transparent'
                        : 'border-border-color text-default hover:bg-light'
                    }`}
                  >
                    <i className="icon icon-plus me-1 text-[11px]" />
                    Add a garment
                  </button>
                </div>

                {wearer.garments.length === 0 && (
                  <p className="text-default text-[12px]">Nothing on this person yet.</p>
                )}

                {wearer.garments.map((garment) => (
                  <GarmentRow
                    key={garment.id}
                    garment={garment}
                    currency={currency}
                    amendment={amendment}
                    swapping={swapping?.id === garment.id}
                    onToggleRemove={() => {
                      setSwapping(null);
                      setAmendment((was) => toggleRemoval(was, garment.id));
                    }}
                    onStartSwap={() => startSwap(garment)}
                    onCancelSwap={() => {
                      setSwapping(null);
                      setAmendment((was) => clearSwap(was, garment.id));
                    }}
                  />
                ))}

                {amendment.additions
                  .filter((addition) => addition.wearerId === wearer.id)
                  .map((addition) => (
                    <IncomingRow
                      key={addition.incoming.unitId}
                      garment={addition.incoming}
                      imageUrl={railUnits.get(addition.incoming.unitId)?.imageUrl ?? null}
                      currency={currency}
                      onTakeBack={() =>
                        setAmendment((was) => dropIncoming(was, addition.incoming.unitId))
                      }
                    />
                  ))}
              </div>
            ))}

            {/* ---- somebody joining the party ---- */}
            {amendment.newWearers.map((wearer) => (
              <div
                key={wearer.key}
                className="border-primary bg-primary-transparent mb-3 rounded border border-dashed p-3"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-gray-900">
                    {wearer.name}
                    <span className="text-primary font-normal"> · joining</span>
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setSwapping(null);
                        setTarget({ kind: 'new', key: wearer.key, name: wearer.name });
                      }}
                      className={`rounded border px-2 py-1 text-[11.5px] ${
                        target?.kind === 'new' && target.key === wearer.key
                          ? 'border-primary text-primary bg-white'
                          : 'border-border-color text-default bg-white hover:bg-light'
                      }`}
                    >
                      <i className="icon icon-plus me-1 text-[11px]" />
                      Add a garment
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setAmendment((was) => removeNewWearer(was, wearer.key));

                        if (target?.kind === 'new' && target.key === wearer.key) {
                          setTarget(null);
                        }
                      }}
                      aria-label={`Remove ${wearer.name}`}
                      className="text-danger hover:bg-danger-transparent flex size-6 items-center justify-center rounded"
                    >
                      <i className="icon icon-trash text-[12px]" />
                    </button>
                  </div>
                </div>

                {wearer.garments.length === 0 ? (
                  <p className="text-default text-[12px]">
                    Pick what they are wearing from the rail below.
                  </p>
                ) : (
                  wearer.garments.map((garment) => (
                    <IncomingRow
                      key={garment.unitId}
                      garment={garment}
                      imageUrl={railUnits.get(garment.unitId)?.imageUrl ?? null}
                      currency={currency}
                      onTakeBack={() => setAmendment((was) => dropIncoming(was, garment.unitId))}
                    />
                  ))
                )}
              </div>
            ))}

            <div className="border-border-color mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
              <div className="min-w-48 flex-1">
                <Field
                  label="Somebody else joining"
                  hint="A cousin added a week before the wedding — they are fitted, they do not pay."
                >
                  <input
                    value={wearerName}
                    onChange={(event) => setWearerName(event.target.value)}
                    placeholder="Name"
                    className={controlClass}
                  />
                </Field>
              </div>

              <Button variant="secondary" disabled={!wearerName.trim()} onClick={addWearer}>
                Add to the party
              </Button>
            </div>
          </Card>

          {/* ---- the rail ---- */}
          <div ref={railRef} className="scroll-mt-4">
            {eachGarment ? (
              <GarmentPicker
                units={units}
                days={booking.chargedDays}
                claimed={claimed}
                currency={currency}
                title={swapping ? `Replacing ${swapping.serialCode}` : 'Add from the rail'}
                searching={searching}
                searched={searched}
                disabled={busy || (!target && !swapping)}
                notice={
                  swap ? (
                    <p className="text-default mb-3 text-[12.5px]">{swap.sentence}</p>
                  ) : (
                    railNotice
                  )
                }
                onPick={pick}
                droppable={droppable}
                onDrop={(unit) => setAmendment((was) => dropIncoming(was, unit.unitId))}
                onChaseHold={onChaseHold}
                toggle={railToggle}
              />
            ) : (
              <DesignRail
                designs={designs}
                units={units}
                days={booking.chargedDays}
                currency={currency}
                claimed={claimed}
                forName={pickingFor}
                searching={searching}
                searched={searched}
                disabled={busy}
                notice={railNotice}
                more={moreDesigns}
                toggle={railToggle}
                replacing={
                  swapping && swap
                    ? {
                        serialCode: swapping.serialCode,
                        sentence: swap.sentence,
                        onCancel: () => setSwapping(null),
                      }
                    : null
                }
                openId={openDesign}
                onOpenChange={setOpenDesign}
                onAdd={pick}
              />
            )}
          </div>
        </div>

        {/* ----------------------------------------------------- right column */}
        <div className="col-span-12 xl:col-span-4">
          <div className="sticky top-4 flex flex-col gap-4">
            <Card>
              <SectionTitle>What changes</SectionTitle>

              {changes.length === 0 ? (
                <p className="text-default py-4 text-center text-[12.5px]">
                  Nothing yet. Drop a garment, swap one, or add to the party.
                </p>
              ) : (
                <div className="mb-3 flex flex-col gap-1.5">
                  {changes.map((change) => (
                    <div
                      key={change.key}
                      className="flex items-start justify-between gap-2 text-[12.5px]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-gray-900">{change.what}</span>
                        <span className="text-default block truncate text-[11px]">
                          {change.kind === 'remove'
                            ? 'Dropped'
                            : change.kind === 'swap'
                              ? 'Swapped'
                              : 'Added'}
                          {change.who && ` · ${change.who}`}
                        </span>
                      </span>

                      <span
                        className={`font-mono-nums shrink-0 ${
                          change.delta === null
                            ? 'text-warning'
                            : change.delta < 0
                              ? 'text-success'
                              : 'text-gray-900'
                        }`}
                      >
                        {change.delta === null
                          ? 'No rate'
                          : `${change.delta < 0 ? '−' : '+'}${currency}${formatAmount(Math.abs(change.delta))}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-border-color flex flex-col gap-1.5 border-t pt-3">
                <Row label="Rental now" value={`${currency}${formatAmount(money.rentalBefore)}`} />
                <Row
                  label="Rental after"
                  value={`${currency}${formatAmount(money.rentalAfter)}`}
                  strong={money.discountAfter <= 0}
                />

                {/* A percentage concession follows the party, so the figure the customer is quoted
                    is not the rental. Showing the discount moving is what stops a clerk reading out
                    the gross delta on a booking that is 10% off. */}
                {money.discountAfter > 0 && (
                  <>
                    <Row
                      label="Discount after"
                      value={`− ${currency}${formatAmount(money.discountAfter)}`}
                    />
                    <Row
                      label="Payable after"
                      value={`${currency}${formatAmount(money.rentalAfter - money.discountAfter)}`}
                      strong
                    />
                  </>
                )}
              </div>

              {/* The deposit sits below its own rule and outside the rental, the same placement
                  NewBookingPage uses and for the same reason: money held is not money earned, and a
                  layout that added it in would look wrong — which is a cheap way to stop an
                  expensive GST error. */}
              {money.depositHeld > 0 && (
                <div className="border-border-color mt-3 flex flex-col gap-1.5 border-t pt-3">
                  <Row
                    label="Deposit held"
                    value={`${currency}${formatAmount(money.depositHeld)}`}
                  />
                  <Row
                    label="Now suggests"
                    value={`${currency}${formatAmount(money.depositSuggested)}`}
                  />
                </div>
              )}

              {/* Shown whenever the shop is short, because that is true whether or not this change
                  caused it — a booking can arrive here already under-secured from an earlier
                  amendment whose top-up was never collected. The *tick* is only demanded when this
                  change makes it worse; asking a clerk to acknowledge a standing gap they are in
                  the middle of reducing is how an acknowledgement stops being read. */}
              {money.depositShortfall > 0 && (
                <Notice
                  tone="warning"
                  title={
                    money.needsAcknowledgement
                      ? 'This change leaves the deposit short'
                      : 'The deposit is already short of this booking'
                  }
                >
                  <p className={money.needsAcknowledgement ? 'mb-2' : ''}>
                    The shop is holding {currency}
                    {formatAmount(money.depositHeld)} against a booking that suggests {currency}
                    {formatAmount(money.depositSuggested)} — {currency}
                    {formatAmount(money.depositShortfall)} short. The figure is <b>not rewritten</b>
                    : the shop physically holds a specific sum, and changing the record would make
                    it disagree with the cash box. Take the difference as a deposit payment.
                  </p>

                  <label
                    className={`flex items-start gap-2 text-[12.5px] ${
                      money.needsAcknowledgement ? '' : 'hidden'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={amendment.acknowledgeShortfall}
                      onChange={(event) =>
                        setAmendment((was) => ({
                          ...was,
                          acknowledgeShortfall: event.target.checked,
                        }))
                      }
                      className="mt-0.5"
                    />
                    <span>Go ahead — I will take the difference separately.</span>
                  </label>
                </Notice>
              )}

              {money.depositHeld > 0 && money.depositShortfall < 0 && (
                <p className="text-default mt-3 text-[11.5px]">
                  The shop is now holding {currency}
                  {formatAmount(Math.abs(money.depositShortfall))} more than this booking suggests.
                  That comes back at settlement, which computes the refund from what is held.
                </p>
              )}
            </Card>

            <Card>
              <SectionTitle>Why</SectionTitle>

              <Field
                label="Reason"
                hint="Required. This is what the shop reads back if the customer disputes what was agreed."
              >
                <textarea
                  rows={3}
                  value={amendment.reason}
                  onChange={(event) =>
                    setAmendment((was) => ({ ...was, reason: event.target.value }))
                  }
                  placeholder="Customer dropped the safa and took the cream sherwani instead"
                  className={controlClass}
                />
              </Field>

              {problems.length > 0 && (
                <Notice tone="warning" title="Not ready yet">
                  <ul className="list-disc ps-4">
                    {problems.map((problem) => (
                      <li key={problem.message}>{problem.message}</li>
                    ))}
                  </ul>
                </Notice>
              )}

              {/* The delta is on the button, because a clerk should never press one whose
                  consequence is not written on it. */}
              <Button
                variant="primary"
                className="mt-3 w-full"
                data-enter-key-commit
                shortcut="save"
                loading={busy}
                disabled={busy || problems.length > 0}
                onClick={() => onApply(amendment)}
              >
                {money.payableDelta === 0
                  ? 'Apply — no change to the money'
                  : money.payableDelta > 0
                    ? `Apply — customer pays ${currency}${formatAmount(money.payableDelta)} more`
                    : `Apply — customer is owed ${currency}${formatAmount(Math.abs(money.payableDelta))}`}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One garment on the booking, with the two things that may be done to it.
 *
 * <p>
 * <strong>Nothing destructive is on a keyboard shortcut here, and that is the rule rather than an
 * omission.</strong> Dropping a garment is a row action and a global key has no row — it would
 * resolve to whichever the DOM offers first, which is how a shortcut drops the wrong thing.
 * </p>
 */
function GarmentRow({
  garment,
  currency,
  amendment,
  swapping,
  onToggleRemove,
  onStartSwap,
  onCancelSwap,
}: {
  garment: BookingGarment;
  currency: string;
  amendment: Amendment;
  /** This row is the one waiting for a replacement to be picked. */
  swapping: boolean;
  onToggleRemove: () => void;
  onStartSwap: () => void;
  onCancelSwap: () => void;
}) {
  const gone = isOffTheBooking(garment.state);
  const dropping = amendment.removals.includes(garment.id);
  const swap = amendment.swaps.find((row) => row.garmentId === garment.id);

  return (
    <div
      className={`border-border-color flex flex-wrap items-center gap-2 border-t py-2 first:border-t-0 ${
        gone || dropping ? 'opacity-55' : ''
      }`}
    >
      <GarmentImage url={garment.imageUrl} alt={garment.serialCode} size="sm" />

      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] text-gray-900">
          {garment.itemName}
          <span className="text-default"> · {garment.variantDisplayName}</span>
        </span>
        <span className="text-default font-mono-nums block truncate text-[11px]">
          {withReference(garment.serialCode, garment.referenceNumber)}
          {garment.rentalRate !== null && ` · ${currency}${formatAmount(garment.rentalRate)}`}
        </span>

        {swap && (
          <span className="text-primary block truncate text-[11px]">
            → {withReference(swap.incoming.serialCode, swap.incoming.referenceNumber)}
            {swap.incoming.rate !== null && ` · ${currency}${formatAmount(swap.incoming.rate)}`}
          </span>
        )}
      </div>

      <StatusBadge tone={garmentTone(garment.state)}>{humanizeEnum(garment.state)}</StatusBadge>

      {/* An amendment already took this one off. It is history now — shown so a shop can see what
          happened, and offered no actions at all. */}
      {gone ? null : (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onToggleRemove}
            disabled={!canRemove(garment.state)}
            title={
              canRemove(garment.state)
                ? undefined
                : `A garment that is ${humanizeEnum(garment.state)} cannot be taken off the booking.`
            }
            className={`rounded border px-2 py-1 text-[11.5px] disabled:cursor-not-allowed disabled:opacity-50 ${
              dropping
                ? 'border-danger text-danger bg-danger-transparent'
                : 'border-border-color text-default hover:bg-light'
            }`}
          >
            {dropping ? 'Keep it' : 'Drop'}
          </button>

          <button
            type="button"
            onClick={swap || swapping ? onCancelSwap : onStartSwap}
            disabled={!canSwap(garment.state)}
            title={
              canSwap(garment.state)
                ? undefined
                : `A garment that is ${humanizeEnum(garment.state)} cannot be swapped.`
            }
            className={`rounded border px-2 py-1 text-[11.5px] disabled:cursor-not-allowed disabled:opacity-50 ${
              swap || swapping
                ? 'border-primary text-primary bg-primary-transparent'
                : 'border-border-color text-default hover:bg-light'
            }`}
          >
            {swap ? 'Undo swap' : swapping ? 'Cancel' : 'Swap'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A garment this amendment brings in, on the person it is for — taken back with one click. The
 * design cards have no un-pick of their own, so this is the way back on that view.
 */
function IncomingRow({
  garment,
  imageUrl,
  currency,
  onTakeBack,
}: {
  garment: IncomingGarment;
  imageUrl: string | null;
  currency: string;
  onTakeBack: () => void;
}) {
  return (
    <div className="border-border-color flex flex-wrap items-center gap-2 border-t py-2 first:border-t-0">
      <GarmentImage url={imageUrl} alt={garment.serialCode} size="sm" />

      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] text-gray-900">{garment.itemName}</span>
        <span className="text-primary font-mono-nums block truncate text-[11px]">
          Coming in · {withReference(garment.serialCode, garment.referenceNumber)}
          {garment.rate !== null && ` · ${currency}${formatAmount(garment.rate)}`}
        </span>
      </div>

      {garment.rate === null && <StatusBadge tone="warning">No rate</StatusBadge>}

      <button
        type="button"
        onClick={onTakeBack}
        className="border-border-color text-default hover:bg-light rounded border px-2 py-1 text-[11.5px]"
      >
        Take back
      </button>
    </div>
  );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 text-[12.5px]">
      <span className="text-default">{label}</span>
      <span
        className={`font-mono-nums ${strong ? 'font-semibold text-gray-900' : 'text-gray-900'}`}
      >
        {value}
      </span>
    </div>
  );
}
